import { Component } from '@theme/component';

const STORAGE_KEY = 'bravident-filters-hidden';

/**
 * <filters-toggle> — toggles visibility of the vertical filter sidebar on the
 * collection page (desktop only). Persists state in localStorage.
 *
 * Expected markup:
 *   <filters-toggle data-target-selector=".collection-wrapper">
 *     <button ref="button" on:click="/toggle" aria-pressed="false">Filters</button>
 *   </filters-toggle>
 */
class FiltersToggleElement extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Restore persisted state (desktop only). Default = sidebar collapsed.
    let hidden = true;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === '0') hidden = false;
    } catch {
      // localStorage may be unavailable in private mode; ignore.
    }

    // Target may not be in the DOM yet if the toggle parsed before the wrapper.
    const tryApply = () => {
      if (this.#target) {
        this.#apply(hidden);
      } else {
        requestAnimationFrame(tryApply);
      }
    };
    tryApply();
  }

  toggle = async (event) => {
    event?.preventDefault?.();

    // Mobile (≤749px): open the right-sliding filters drawer instead of toggling the inline sidebar
    if (window.matchMedia('(max-width: 749px)').matches) {
      // dialog.js and filters-toggle.js are both fetchpriority="low" type="module".
      // On slow real-device networks/CPUs, the user can tap before <dialog-component>
      // is upgraded — in which case getElementById('filters-drawer').showDialog is undefined.
      // Wait for the custom element to be defined before reading the method.
      await customElements.whenDefined('dialog-component');
      const drawer = document.getElementById('filters-drawer');
      if (drawer && typeof drawer.showDialog === 'function') {
        drawer.showDialog();
      }
      return;
    }

    const target = this.#target;
    if (!target) return;

    const next = !target.classList.contains('collection-wrapper--filters-hidden');
    this.#apply(next);

    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  get #target() {
    const selector = this.dataset.targetSelector || '.collection-wrapper';
    return document.querySelector(selector);
  }

  /** @param {boolean} hidden */
  #apply(hidden) {
    const target = this.#target;
    if (!target) return;
    target.classList.toggle('collection-wrapper--filters-hidden', hidden);

    const button = /** @type {HTMLButtonElement | undefined} */ (this.refs.button);
    if (button) {
      button.setAttribute('aria-pressed', hidden ? 'false' : 'true');
    }
    this.setAttribute('data-hidden', hidden ? 'true' : 'false');
  }
}

if (!customElements.get('filters-toggle')) {
  customElements.define('filters-toggle', FiltersToggleElement);
}
